import { Suspense, useState } from "react"
import { FormProvider, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { DetailCheckbox } from "@/components/ui/checkbox"
import { FormField } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import CareProviderCloseCard from "@/modules/care-provider/components/edit-care-provider/care-provider-close-card"
import CareProviderSendToAdminCard from "@/modules/care-provider/components/edit-care-provider/care-provider-send-to-admin-card"
import { CareProviderSchemaType } from "@/modules/care-provider/components/edit-care-provider/schema/care-provider-schema"
import { CareProviderWithOrigin } from "@/modules/care-provider/services/useCareProviderWithOrigin"
import { CitySelector } from "@/modules/patients/components/city-selector"
import { CountrySelector } from "@/modules/patients/components/country-selector"
import { PhoneNumberInput } from "@/modules/patients/components/phoneNumber/PhoneNumberInput"
import { PostalCodeSelector } from "@/modules/patients/components/postalcode-selector"

export type CareProvider = CareProviderWithOrigin | null

type CareProviderPracticeTabProps = {
  careProvider: CareProvider
  form: UseFormReturn<CareProviderSchemaType>
  onCloseMutationCompleted: VoidFunction
}

const CareProviderPracticeTab = ({ careProvider, form, onCloseMutationCompleted }: CareProviderPracticeTabProps) => {
  const { t } = useTranslation()
  const [permanentlyClosed, setPermanentlyClosed] = useState<boolean>(!careProvider?.isActive)

  const {
    control,
    register,
    setValue,
    formState: { errors },
  } = form

  return (
    <div className="grid w-full grid-cols-5 gap-8">
      <FormProvider {...form}>
        <div className="col-span-3 grid gap-4">
          <div className="pointer-events-none grid w-full grid-cols-2 gap-4 opacity-50">
            <Input
              disabled
              className="text-body-sm text-on-surface-subdued"
              testId="title"
              label={t("common.title")}
              defaultValue={careProvider?.title ?? ""}
              title={careProvider?.title ?? ""}
            />
            <Input
              disabled
              testId="businessName"
              label={t("admin.careProviders.dialog.update.basic.businessName")}
              wrapperProps={{ className: "col-span-1" }}
              defaultValue={careProvider?.businessName ?? ""}
              title={careProvider?.businessName ?? ""}
            />
          </div>

          <div className="grid w-full grid-cols-2 gap-4 opacity-50">
            <Input
              disabled
              testId="firstName"
              label={t("admin.careProviders.dialog.update.basic.givenName")}
              wrapperProps={{ className: "col-span-1" }}
              defaultValue={careProvider?.firstName ?? ""}
              title={careProvider?.firstName ?? ""}
            />
            <Input
              disabled
              testId="lastName"
              label={t("admin.careProviders.dialog.update.basic.familyName")}
              wrapperProps={{ className: "col-span-1" }}
              defaultValue={careProvider?.lastName ?? ""}
              title={careProvider?.lastName ?? ""}
            />
          </div>
          <div className="grid w-full grid-cols-2 gap-4 opacity-50">
            <Input
              disabled
              testId="zsrNumber"
              label={t("admin.careProviders.dialog.update.basic.zsrNumber")}
              wrapperProps={{ className: "col-span-1" }}
              defaultValue={careProvider?.creditorCodeRegister ?? ""}
              title={careProvider?.creditorCodeRegister ?? ""}
            />
            <Input
              disabled
              testId="glnNumber"
              label={t("admin.careProviders.dialog.update.basic.glnNumber")}
              wrapperProps={{ className: "col-span-1" }}
              defaultValue={careProvider?.globalLocationNumber ?? ""}
              title={careProvider?.globalLocationNumber ? careProvider?.globalLocationNumber + "" : ""}
            />
          </div>
          <p className="text-on-surface text-base font-medium">
            {t("admin.careProviders.dialog.update.address.sectionTitle")}
          </p>

          <div className="pointer-events-none grid gap-4 opacity-50">
            <Suspense fallback={null}>
              <CountrySelector />
              <PostalCodeSelector label={t("common.postalCode")} />
              <CitySelector />
            </Suspense>
            <Input
              disabled
              testId="streetAddress"
              label={t("patient.header.emergencyServiceDialog.streetAddress")}
              defaultValue={careProvider?.streetAddress ?? ""}
              title={careProvider?.streetAddress ?? ""}
              wrapperProps={{ className: "col-span-4" }}
            />
            <Input
              disabled
              testId="additionalAddress"
              label={t("admin.careProviders.dialog.update.address.additionalAddress")}
              defaultValue={careProvider?.additionalAddress ?? ""}
              title={careProvider?.additionalAddress ?? ""}
              wrapperProps={{ className: "col-span-4" }}
            />
          </div>

          <p className="text-on-surface text-base font-medium">
            {t("admin.careProviders.dialog.update.contact.sectionTitle")}
          </p>
          <div className="grid w-full grid-cols-2 gap-4">
            <Input
              testId="email"
              label={t("common.email")}
              wrapperProps={{ className: "col-span-1" }}
              translateError
              error={errors.email?.message}
              {...register("email")}
              onBlur={(e) => {
                if (e.target.value.length === 0)
                  setValue("email", careProvider?.originEmail, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
              }}
            />
            <FormField
              name="phoneNumber"
              control={control}
              render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
                <PhoneNumberInput
                  ref={ref}
                  label={t("common.phoneNumber")}
                  value={value ?? undefined}
                  onChange={(value) => onChange(value ?? "")}
                  error={t(error?.message ?? "")}
                  isSMSSupported
                  classes={{ buttonClass: "h-12", inputClass: "h-12" }}
                  originalValue={careProvider?.originPhoneNumber ?? undefined}
                />
              )}
            />
          </div>
          <Input
            testId="website"
            label={t("common.website")}
            wrapperProps={{ className: "col-span-1" }}
            translateError
            error={errors.website?.message}
            {...register("website")}
            onBlur={(e) => {
              if (e.target.value.length === 0)
                setValue("website", careProvider?.originWebsite, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
            }}
          />

          <p className="text-on-surface text-base font-medium">
            {t("admin.careProviders.dialog.update.admission.sectionTitle")}
          </p>
          <div className="mb-4.5 grid grid-cols-2 gap-4">
            <FormField
              control={control}
              name="isMedgatePartnerNetworkMember"
              render={({ field: { value, onChange } }) => (
                <DetailCheckbox checked={value} onCheckedChange={onChange}>
                  {t("admin.careProviders.dialog.update.admission.medgatePartnerNetwork")}
                </DetailCheckbox>
              )}
            />
            <FormField
              control={control}
              name="isTakesNewPatients"
              render={({ field: { value, onChange } }) => (
                <DetailCheckbox checked={value} onCheckedChange={onChange}>
                  {t("admin.careProviders.dialog.update.admission.takesNewPatients")}
                </DetailCheckbox>
              )}
            />
          </div>
        </div>
      </FormProvider>
      <div className="col-span-2 grid">
        <div className="flex flex-col gap-4">
          <div className="border-border rounded-lg border px-1.5">
            <CareProviderSendToAdminCard careProviderId={careProvider?.id} permanentlyClosed={permanentlyClosed} />
          </div>
          <div className="border-border rounded-lg border px-1.5">
            <CareProviderCloseCard
              careProvider={careProvider}
              permanentlyClosed={permanentlyClosed}
              onCloseMutationCompleted={() => {
                setPermanentlyClosed(true)
                onCloseMutationCompleted()
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export { CareProviderPracticeTab }
